import React, { useEffect, useMemo, useState } from 'react';
import { ChevronRight, Search } from 'lucide-react';
import {
  getVisibleItems,
  CATEGORY_ORDER,
  CATEGORY_LABELS,
  type NavItem,
} from '@/components/navigation';
import { useAuth } from '@/context/AuthContext';
import { HexFieldBackdrop } from '@/components/brand/BrandMarks';
import { YourCampaignsCard } from '@/components/YourCampaignsCard';
import { roleMatches } from '@/lib/roles';
import { fetchPendingInvites } from '@/api/adminUsers';
import { previewCloseStale } from '@/api/easy';
import { listMyCandidates, listCandidateStaff } from '@/api/delegation';
import { fetchVoterStats } from '@/api/voters';

interface HomeViewProps {
  onNavigate: (viewKey: string) => void;
}

/**
 * Home — the role-aware launcher every non-canvasser lands on.
 *
 * Three bands, most actionable first:
 *   1. "Pick up where you left off" — the last view this browser had open.
 *   2. One-tap plays — the Easy Buttons, deep-linked into their host views.
 *   3. The full toolbox — every nav item this role can open, searchable and
 *      grouped by category. The hamburger's catalog promoted to a screen.
 *
 * Every tile flows through getVisibleItems, so a tile can never point at an
 * endpoint that 403s (NAV_ENDPOINT_GATES suite). The only fetches are the
 * play count badges: fired once on mount, only for plays the role already
 * passed both fences for, and any error just leaves the badge off — the play
 * stays a plain deep-link. Canvassers never land here — their home is their
 * doors (homeViewForRole).
 */

export const LAST_VIEW_STORAGE_KEY = 'pcgop:last-view';

/**
 * Views that need drill params and cannot be re-entered from a cold link.
 * ('canvass' is NOT here: it renders fine cold, and the recording site
 * already skips whatever the role's own home view is.)
 */
export const LAST_VIEW_EXCLUDED = new Set([
  'home',
  'ld',
  'subregion',
  'precinct',
  'campaign-detail',
  'routes',
]);

interface Play {
  key: string;
  /** The view the play lives in — must be visible to the role to render. */
  target: string;
  title: string;
  description: string;
  /** Extra role fence beyond target visibility, when the in-view action is narrower. */
  roles?: string[];
  /**
   * How many items a press would act on right now — the preview's `eligible`,
   * never `considered`, so the badge and the button inside the host view
   * agree. Only called for plays that already passed both role fences.
   */
  fetchCount?: (signal: AbortSignal) => Promise<number>;
}

// The shipped Easy Buttons, phrased as the job they do. Each card is a
// deep-link to the view that hosts the action — the action's own gate lives
// at its call site; `roles` here only keeps a card from rendering for someone
// who could open the view but never press the button inside it.
const PLAYS: Play[] = [
  {
    key: 'cover-gaps',
    target: 'coverage',
    title: 'Cover the gaps',
    description: 'Plan routes for the five worst-covered precincts',
    roles: ['ADMIN', 'ORG_MANAGER', 'MANAGER', 'COORDINATOR'],
  },
  {
    key: 'staff-weekend',
    target: 'routes-list',
    title: 'Staff the weekend',
    description: 'Match open routes to your available canvassers',
    roles: ['ADMIN', 'ORG_MANAGER', 'MANAGER', 'COORDINATOR'],
  },
  {
    key: 'close-day',
    target: 'canvass-review',
    title: 'Close out the day',
    description: 'Sweep stale shifts and review what came back',
    roles: ['ADMIN', 'ORG_MANAGER', 'MANAGER', 'COORDINATOR'],
    fetchCount: (signal) =>
      previewCloseStale({}, signal).then((r) => r.proposal.eligible),
  },
  {
    key: 'chase-invites',
    target: 'users',
    title: 'Chase the invites',
    description: 'Re-send links nobody has redeemed yet',
    roles: ['ADMIN', 'ORG_MANAGER'],
    fetchCount: (signal) =>
      fetchPendingInvites({}, signal).then((r) => r.proposal.eligible),
  },
  {
    key: 'my-team',
    target: 'my-staff',
    title: 'Your team',
    description: 'Volunteers and committee staff working your race',
    // CANDIDATE-only, like the my-staff tile itself. The badge is the roster
    // headcount (excluding the candidate's own row) rather than an eligible
    // count — for a summary card the "items a press would show" IS the team.
    // Two chained reads, but only for candidates, who have no other plays.
    roles: ['CANDIDATE'],
    fetchCount: async (signal) => {
      const own = (await listMyCandidates(signal)).find(
        (r) => r.relation === 'candidate',
      );
      if (!own) return 0;
      const roster = await listCandidateStaff(own.candidate_id, signal);
      return roster.members.filter((m) => m.relation !== 'candidate').length;
    },
  },
];

/**
 * Inventory counts for toolbox TILES — the plays machinery's quieter sibling.
 * A play badge means "work is waiting"; a tile count means "this is how much
 * is in there" (the voter roll in YOUR turf, thanks to the server's region
 * filter — a scoped canvasser would see their turf's number, a county-wide
 * candidate the county's). Same contract as plays: fetched once, only when
 * the tile is visible AND the roles fence passes, zero/error → no count.
 */
interface TileCount {
  /**
   * Mirror of the ENDPOINT's role set where it is narrower than the tile's —
   * `voters` (the tile) is PII_READ_ROLES which includes CANVASSER, but
   * /voters/stats is ANALYTICS_READ_ROLES which does not, so without this
   * fence every canvasser's Home would fire a guaranteed 403.
   */
  roles?: string[];
  fetchCount: (signal: AbortSignal) => Promise<number>;
}

const TILE_COUNTS: Record<string, TileCount> = {
  voters: {
    // models/user.py::ANALYTICS_READ_ROLES, uppercased for roleMatches.
    roles: [
      'ADMIN', 'ORG_MANAGER', 'MANAGER', 'ANALYST', 'COORDINATOR',
      'VIEWER', 'SMS_MANAGER', 'MESSENGER', 'CANDIDATE',
    ],
    fetchCount: (signal) => fetchVoterStats(signal).then((s) => s.total_voters),
  },
};

/** 79933 → "79.9K" — a tile count is a magnitude, not a figure to transcribe. */
function formatCompact(n: number): string {
  return new Intl.NumberFormat('en', {
    notation: 'compact',
    maximumFractionDigits: 1,
  }).format(n);
}

function greetingForHour(hour: number): string {
  if (hour < 5) return 'Working late';
  if (hour < 12) return 'Good morning';
  if (hour < 17) return 'Good afternoon';
  return 'Good evening';
}

function firstNameOf(fullName?: string | null, email?: string | null): string | null {
  const fromName = fullName?.trim().split(/\s+/)[0];
  if (fromName) return fromName;
  const fromEmail = email?.split('@')[0];
  return fromEmail || null;
}

export function readLastView(
  visibleKeys: Set<string>,
): { key: string; label: string } | null {
  try {
    const raw = localStorage.getItem(LAST_VIEW_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (
      typeof parsed?.key !== 'string' ||
      typeof parsed?.label !== 'string' ||
      LAST_VIEW_EXCLUDED.has(parsed.key) ||
      !visibleKeys.has(parsed.key)
    ) {
      return null;
    }
    return { key: parsed.key, label: parsed.label };
  } catch {
    return null;
  }
}

export const HomeView: React.FC<HomeViewProps> = ({ onNavigate }) => {
  const { user } = useAuth();
  const [query, setQuery] = useState('');

  const visibleItems = useMemo(
    () => getVisibleItems(user?.role ?? '').filter((item) => item.key !== 'home'),
    [user?.role],
  );
  const visibleKeys = useMemo(
    () => new Set(visibleItems.map((i) => i.key)),
    [visibleItems],
  );

  const lastView = useMemo(() => readLastView(visibleKeys), [visibleKeys]);

  const plays = useMemo(
    () =>
      PLAYS.filter(
        (play) =>
          visibleKeys.has(play.target) &&
          (!play.roles || roleMatches(user?.role, play.roles)),
      ),
    [visibleKeys, user?.role],
  );

  // Live counts for the plays that can cheaply ask "how many right now?".
  // Fired once per (role-filtered) play list — the fences above have already
  // run, so a viewer never even attempts an admin endpoint. Zero and error
  // both mean "no badge": a play with nothing waiting is still a doorway,
  // and a failed count must not make the card look broken.
  const [playCounts, setPlayCounts] = useState<Record<string, number>>({});
  useEffect(() => {
    const controller = new AbortController();
    setPlayCounts({});
    for (const play of plays) {
      if (!play.fetchCount) continue;
      play
        .fetchCount(controller.signal)
        .then((count) => {
          if (!controller.signal.aborted && count > 0) {
            setPlayCounts((prev) => ({ ...prev, [play.key]: count }));
          }
        })
        .catch(() => {});
    }
    return () => controller.abort();
  }, [plays]);

  // Tile inventory counts — same lifecycle as playCounts above.
  const [tileCounts, setTileCounts] = useState<Record<string, number>>({});
  useEffect(() => {
    const controller = new AbortController();
    setTileCounts({});
    for (const [key, tile] of Object.entries(TILE_COUNTS)) {
      if (!visibleKeys.has(key)) continue;
      if (tile.roles && !roleMatches(user?.role, tile.roles)) continue;
      tile
        .fetchCount(controller.signal)
        .then((count) => {
          if (!controller.signal.aborted && count > 0) {
            setTileCounts((prev) => ({ ...prev, [key]: count }));
          }
        })
        .catch(() => {});
    }
    return () => controller.abort();
  }, [visibleKeys, user?.role]);

  const normalizedQuery = query.trim().toLowerCase();
  const groups = useMemo(() => {
    const matches = normalizedQuery
      ? visibleItems.filter(
          (item) =>
            item.label.toLowerCase().includes(normalizedQuery) ||
            (item.description ?? '').toLowerCase().includes(normalizedQuery),
        )
      : visibleItems;

    return CATEGORY_ORDER.map((category) => ({
      category,
      label: CATEGORY_LABELS[category],
      items: matches.filter((item) => item.category === category),
    })).filter((group) => group.items.length > 0);
  }, [visibleItems, normalizedQuery]);

  const firstName = firstNameOf(user?.full_name, user?.email);
  const now = new Date();
  const dateLine = now.toLocaleDateString(undefined, {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
  });

  return (
    <div className="space-y-8">
      {/* Greeting — the one place the Home screen wears the brand texture:
          a faint hex field fading in from the top-right, behind the text. */}
      <header className="relative -mx-4 px-4 pt-1 pb-2 sm:-mx-6 sm:px-6">
        <HexFieldBackdrop />
        <div className="relative">
        <p className="text-sm text-fg-muted">{dateLine}</p>
        <h1 className="text-2xl sm:text-3xl font-bold text-fg-primary mt-1">
          {greetingForHour(now.getHours())}
          {firstName ? `, ${firstName}` : ''}
        </h1>
        {lastView && (
          <button
            onClick={() => onNavigate(lastView.key)}
            className="mt-3 inline-flex items-center gap-1.5 text-sm font-medium bg-bg-raised hover:bg-bg-elevated border border-border-base rounded-full pl-4 pr-3 min-h-11 transition-colors cursor-pointer"
          >
            <span className="text-fg-secondary">Pick up where you left off</span>
            <span className="text-fg-primary font-semibold">{lastView.label}</span>
            <ChevronRight className="w-4 h-4 text-fg-muted" aria-hidden="true" />
          </button>
        )}
        </div>
      </header>

      {/* Which races this account is attached to. Renders nothing for the
          many users with no candidate links, so it costs the admin screen
          nothing; it is the only place a two-race volunteer can see both. */}
      <YourCampaignsCard />

      {/* One-tap plays */}
      {plays.length > 0 && (
        <section aria-labelledby="home-plays-heading">
          <h2
            id="home-plays-heading"
            className="text-xs font-semibold uppercase tracking-wider text-fg-muted mb-3"
          >
            One-tap plays
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {plays.map((play) => (
              <button
                key={play.key}
                onClick={() => onNavigate(play.target)}
                className="group text-left flex items-center gap-3 rounded-xl border border-border-base bg-bg-raised hover:bg-bg-elevated hover:border-accent/40 p-4 min-h-[4.5rem] transition-colors cursor-pointer"
              >
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-fg-primary">{play.title}</p>
                  <p className="text-sm text-fg-muted mt-0.5">{play.description}</p>
                </div>
                {playCounts[play.key] !== undefined && (
                  <span
                    className="inline-flex items-center rounded-full bg-status-warning-bg/15 text-status-warning border border-status-warning-bg/30 px-2 py-0.5 text-xs font-semibold flex-shrink-0"
                    title={`${playCounts[play.key]} waiting`}
                  >
                    {playCounts[play.key]}
                    <span className="sr-only"> waiting</span>
                  </span>
                )}
                <ChevronRight
                  className="w-5 h-5 flex-shrink-0 text-fg-muted group-hover:text-accent-fg transition-colors"
                  aria-hidden="true"
                />
              </button>
            ))}
          </div>
        </section>
      )}

      {/* Toolbox */}
      <section aria-labelledby="home-toolbox-heading">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
          <h2
            id="home-toolbox-heading"
            className="text-xs font-semibold uppercase tracking-wider text-fg-muted"
          >
            Your tools
          </h2>
          <div className="relative sm:w-72">
            <Search
              className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-fg-muted pointer-events-none"
              aria-hidden="true"
            />
            <input
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Find a tool…"
              aria-label="Find a tool"
              className="w-full min-h-11 pl-9 pr-3 rounded-lg bg-bg-raised border border-border-base text-base text-fg-primary placeholder:text-fg-muted"
            />
          </div>
        </div>

        {groups.length === 0 ? (
          <p className="text-sm text-fg-muted py-8 text-center">
            Nothing matches “{query.trim()}”. Try a different word, or clear the
            search to see everything you have access to.
          </p>
        ) : (
          <div className="space-y-6">
            {groups.map((group) => (
              <div key={group.category}>
                <h3 className="text-sm font-semibold text-fg-secondary mb-2">
                  {group.label}
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-2.5">
                  {group.items.map((item: NavItem) => (
                    <button
                      key={item.key}
                      onClick={() => onNavigate(item.key)}
                      className="group text-left flex items-center gap-3 rounded-lg border border-border-subtle bg-bg-raised hover:bg-bg-elevated p-3 min-h-14 transition-colors cursor-pointer"
                    >
                      <span className="flex-shrink-0 w-9 h-9 rounded-md bg-bg-elevated group-hover:bg-bg-base flex items-center justify-center transition-colors">
                        <item.Icon
                          className="w-[18px] h-[18px] text-fg-secondary"
                          aria-hidden="true"
                        />
                      </span>
                      <span className="flex-1 min-w-0">
                        <span className="block font-medium text-sm text-fg-primary">
                          {item.label}
                        </span>
                        {item.description && (
                          <span className="block text-xs text-fg-muted truncate">
                            {item.description}
                          </span>
                        )}
                      </span>
                      {tileCounts[item.key] !== undefined && (
                        // Neutral tones, unlike the play badge's warning pill:
                        // this is inventory, not work waiting.
                        <span
                          className="inline-flex items-center rounded-full bg-bg-elevated text-fg-secondary border border-border-base px-2 py-0.5 text-xs font-semibold flex-shrink-0"
                          title={`${tileCounts[item.key].toLocaleString()} in your turf`}
                        >
                          {formatCompact(tileCounts[item.key])}
                          <span className="sr-only"> in your turf</span>
                        </span>
                      )}
                      {/* Always visible on touch; hover-reveal is sm+ only —
                          an affordance a phone cannot hover into is invisible. */}
                      <ChevronRight
                        className="w-4 h-4 flex-shrink-0 text-fg-muted opacity-100 sm:opacity-0 sm:group-hover:opacity-100 sm:group-focus-within:opacity-100 transition-opacity"
                        aria-hidden="true"
                      />
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
};
